/**
 * @module lib/election/lead-data-helpers
 * @description Helper functions for extracting information/status from leading/elected election info.
 * from the canada_small_grouped.json feed.
 */

import { getProp } from "utils/utils";
import logger from "utils/logger";

const debugTag = "[ELECTION-LEAD-DATA-HELPERS]";

/**
 * Returns status of party result
 * @param {Object} result - JSON containing party result
 * @returns {String} "complete" if election results are final; "partial" if some results are available; and "" if no results
 */
export function getStatus(result) {
  // Check if data has flowed in.  Polls reported may be 0% but election has started due to rounding (e.g., 0.6%).
  const leadingPartyData = [...getProp(result, "Leading.Party", [])];
  const dataHasComeIn = hasData(leadingPartyData);

  logger.info(`${debugTag} leadingPartyData`, leadingPartyData);
  logger.info(`${debugTag} dataHasComeIn`, dataHasComeIn);

  // Note some cases we need to handle:
  // 1. PercentageOfPollsReporting is 99% but RidingsElected: 338
  // 2. PercentageOfPollsReporting is 100% (maybe just 99.6% but rounding as 100%) but RidingsElected: 337
  // 3. PercentageOfPollsReporting is 100% (true 100%--all polls are reported) but RidingsElected: 337 (because there are some issues with certain ridings so winner party cannot be declared)

  if (result.PercentageOfPollsReporting === 100
    || (Number(result.RidingsElected) !== 0 && result.RidingsElected === result.TotalRidings)) { // Election results complete
    return "complete";
  } else if (dataHasComeIn || (Number(result.RidingsElected) !== 0 && result.RidingsElected !== result.TotalRidings)
    || (result.PercentageOfPollsReporting > 0 && result.PercentageOfPollsReporting < 100)) {
    // The second condition above takes into account the scenario when the election has started
    // but nobody has been elected yet.
    return "partial";
  } else {
    return "";
  }
}

/**
 * Returns whether or not leadingPartyData represents data that has already flowed in (i.e., not in no results state).
 * @param {Array<Object>} leadingPartyData
 * @returns {Boolean} true if LeadingElected data has flowed in; otherwise, false (i.e. in no results state)
 */
export function hasData(leadingPartyData) {
  let hasLeadingPartyData = false;

  if (!leadingPartyData) {
    return false;
  }

  leadingPartyData.forEach((partyData) => {
    // In no results state, LeadingElected does not exist, so it will default to null.
    const leadingElectedVal = getProp(partyData, "LeadingElected", null);

    // Technically, the value won't exist at all if the election is in no results state but adding the 0 check just in case.
    if (leadingElectedVal && leadingElectedVal !== 0) {
      logger.info(`${debugTag} true`, partyData);
      hasLeadingPartyData = true;
    }
  });

  logger.info(`${debugTag} hasLeadingPartyData`, hasLeadingPartyData);

  return hasLeadingPartyData;
}

/**
 * Compares resultA's leading + elected seats with resultB's leading + elected seats.
 * @param {Object} resultA - partyA's leading + elected results
 * @param {Object} resultB - partyB's leading + elected results
 * @returns {Number} -1 if resultA seats > result B seats, 1 if resultA seats < resultB seats, 0 if equal
 */
export function compareResults(resultA, resultB) {
  const numSeatsPartyA = Number(resultA.LeadingElected);
  const numSeatsPartyB = Number(resultB.LeadingElected);

  if (!Number.isNaN(numSeatsPartyA) && !Number.isNaN(numSeatsPartyB)) {
    if (numSeatsPartyA > numSeatsPartyB) {
      return -1;
    }

    if (numSeatsPartyA < numSeatsPartyB) {
      return 1;
    }
  }

  return 0;
}
